import React, { useCallback, useMemo, useEffect, useContext, useState, useDeferredValue, useRef } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  ThemeProvider,
  Stack,
  Chip,
  TextField,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { useTimelineInfiniteQuery } from '../Hooks/useTimelineInfiniteQuery';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { useTimelineSelection } from '../Hooks/useTimelineSelection';
import { useTimelineTable } from '../Hooks/useTimelineTable';
import { useTimelineData } from '../Hooks/useTimelineData';
import { useInfiniteScroll } from '../Hooks/useInfiniteScroll';
import { useEventTypes } from '../Hooks/useEventTypes';
import { useTableFilterOptions } from '../Hooks/useTableFilterOptions';
import { useTableColumns } from '../Hooks/useTableColumns';
import { TimelineCard } from './TimelineCard';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import TimelinePagination from './TimelinePagination';
import { LoadingState, ErrorState, EmptyState } from '../common/States/States';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import { useBuslogPrepare } from '../Hooks/useBuslogPrepare';
import { useBuslogSignals } from '../Hooks/useBuslogSignals';
import { usePlayback } from '../Hooks/usePlayback';
import { getPlaybackTimestamp } from '../VisualizeData/visualizeData.constants';
import BuslogPanels from '../VisualizeData/BuslogPanels';
import SignalSearch from '../VisualizeData/SignalSearch';

import './TimeLineComponent.css';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import EventIcon from '@mui/icons-material/Event';
import CategoryIcon from '@mui/icons-material/Category';
import LabelIcon from '@mui/icons-material/Label';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSavedPreferences } from '../Hooks/useSavedPreferences';
import { useUserPreferences } from '../Hooks/useUserPreferences';

const PREF_STORAGE_KEY = 'timeline_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
};

export const TimelinePage = (props) => {
  const filterConfig = useMemo(() => ({
    fields: {
      vin: createFieldConfig('vin', { type: 'array', defaultValue: [] }),
      mac_id: createFieldConfig('mac_id', { type: 'array', defaultValue: [] }),
      event_type: createFieldConfig('event_type', { type: 'json', defaultValue: [] }),
    },
    dateFields: [
      createDateConfig('start_datetime', 'end_datetime', {
        startParam: 'startDate',
        endParam: 'endDate',
        allowEmpty: true
        })
    ],
    debounceDelay: 300
  }), []);

  const {
    loadSavedPreferences,
    savePreferences,
    unsavePreferences,
    hasSavedPreferences,
    enabledFields,
    isPreferenceEnabled,
  } = useSavedPreferences(PREF_STORAGE_KEY, DEFAULT_FIELDS);

  const { savePreferencesToApi } = useUserPreferences();

  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handleDateRangeChange,
    setFilters
  } = useUrlFilters(filterConfig);

  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [viewMode, setViewMode] = useState('files');
  const [signalFilePath, setSignalFilePath] = useState('');
  const [buslogSearchTerm, setBuslogSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');

  const ordering = sortDirection === 'desc' ? '-datetime' : 'datetime';

  const toggleSortDirection = useCallback(() => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  }, []);


  const prep = useBuslogPrepare(signalFilePath, { enabled: viewMode === 'signals' });
  const signals = useBuslogSignals(signalFilePath);

  const totalDurationSec = useMemo(() => {
    if (!prep.end_ts || !prep.start_ts) return 0;
    return (prep.end_ts - prep.start_ts) / 1e9;
  }, [prep.end_ts, prep.start_ts]);
  
  const playback = usePlayback(totalDurationSec);

  const playbackTimestamp = useMemo(() => {
    if (!prep.start_ts) return '';
    return getPlaybackTimestamp(prep.start_ts, playback.currentTimeSec);
  }, [prep.start_ts, playback.currentTimeSec]);


  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlVin = urlParams.get('vin');
    const hasUrlMacId = urlParams.get('mac_id');
    
    if (!hasUrlVin && !hasUrlMacId) {
      const saved = loadSavedPreferences();
      if (saved.vin?.length > 0 || saved.mac_id?.length > 0) {
        setFilters((prev) => ({
          ...prev,
          vin: saved.vin || [],
          mac_id: saved.mac_id || [],
        }));
      }
    }
  }, []);

  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const handleViewModeChange = useCallback((value) => {
    setViewMode(value);
  }, []);

  const handleSignalFilePathChange = useCallback((newPath) => {
    if (newPath !== signalFilePath) {
      setSignalFilePath(newPath);
      signals.clearSignals();
    }
  }, [signalFilePath, signals.clearSignals]);

  const handleBuslogPreview = useCallback(({ filename, category }) => {
    if (category === 'buslog') {
      setSignalFilePath(filename);
      signals.clearSignals();
      setViewMode('signals');
      return true;
    }
    return false;
  }, [signals.clearSignals]);

  const { selectedItem, handleTimelineItemClick, clearSelection } = useTimelineSelection();
  const deferredSelectedItem = useDeferredValue(selectedItem);

  useEffect(() => {
    const isVinEmpty = !filters.vin || (Array.isArray(filters.vin) && filters.vin.length === 0);
    const isMacIdEmpty = !filters.mac_id || (Array.isArray(filters.mac_id) && filters.mac_id.length === 0);
    if ((isVinEmpty && isMacIdEmpty) || (selectedItem && (isVinEmpty || isMacIdEmpty))) {
      clearSelection();
    }
  }, [filters.vin, filters.mac_id, clearSelection]);

  const {
    timelineTableQuery,
    currentPage,
    offsetMinutes,
    setOffsetMinutes,
    tableFilters,
    goToNextPage,
    goToPreviousPage,
    handleTableFilterChange,
  } = useTimelineTable(filters, deferredSelectedItem, debouncedFilters, dateType);

  const {
    rawData,
    fetchNextPage,
    hasNextPage: hasInfiniteNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isEmpty,
    hasData,
    totalCount,
    error,
  } = useTimelineInfiniteQuery(debouncedFilters, createIsoStringWithTimezone, dateType, ordering);

  const { timelineData } = useTimelineData(rawData);
  const { loadMoreRef } = useInfiniteScroll(fetchNextPage, hasInfiniteNextPage, isFetchingNextPage, hasData);

  const { eventTypesQuery, hasVinOrMacId, eventTypes } = useEventTypes(debouncedFilters, createIsoStringWithTimezone, dateType);
  const { tableFilterOptionsQuery, categories, types } = useTableFilterOptions(deferredSelectedItem, dateType);
  const { tableColumns } = useTableColumns(formatDateTime);

  const filterOptions = useMemo(() => ({
    eventTypes: eventTypes || [],
    categories: categories || [],
    types: types || []
  }), [eventTypes, categories, types]);

  const handleEventTypeChange = useCallback((newValue) => {
    const transformedValue = (newValue || []).map(option => ({
      value: option.value,
      type: option.type,
      status: option.status || ""
    }));
    handleFilterChange('event_type', transformedValue);
  }, [handleFilterChange]);

  const highlightRowFn = useCallback((rowData) => {
    if (!selectedItem || !rowData) return false;
    const eventFile = selectedItem.file_location;
    if (eventFile) {
      const rowFile = rowData.file_location || rowData.filename;
      return rowFile === eventFile;
    }
    if (selectedItem.datetime && rowData.datetime) {
      return rowData.datetime === selectedItem.datetime;
    }
    return false;
  }, [selectedItem]);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (timelineTableQuery.isFetching) return;
    const timer = setTimeout(() => {
      const container = tableContainerRef.current;
      if (!container) return;
      const highlighted = container.querySelector('tr[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [timelineTableQuery.data, timelineTableQuery.isFetching]);

  const { table, setRowSelection } = useReusableTable({
    columns: tableColumns,
    data: timelineTableQuery.data?.data?.results || [],
    rowCount: timelineTableQuery.data?.data?.count || 0,
    getRowId: (row) => row.id ?? `${row.filename}`,
    onPreview: handleBuslogPreview,
    enableRowSelection: true,
    enableRowActions: true,
    enableDefaultPreview: true,
    enableDefaultDownload: true,
    enableDefaultAddToCart: true,
    maxHeight: "100%",
    isLoading: timelineTableQuery.isFetching,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enableColumnOrdering: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enablePagination: false,
    enableHiding: true,
    highlightRowFn,
    renderTopToolbarCustomActions: () => (
      <Box className="table-top-toolbar">
        <Typography variant="h6" className="table-title">
          Timeline Files
        </Typography>
      </Box>
    ),
    renderBottomToolbarCustomActions: () => (
      <TimelinePagination
        currentPage={currentPage}
        offsetMinutes={offsetMinutes}
        setOffsetMinutes={setOffsetMinutes}
        goToPreviousPage={goToPreviousPage}
        goToNextPage={goToNextPage}
        timelineTableQuery={timelineTableQuery}
      />
    ),
    muiTableProps: { className: 'mui-table' },
    muiTableContainerProps: { className: 'mui-table-container' },
    initialState: { density: 'compact' },
  });

  useEffect(() => {
    setRowSelection({});
  }, [selectedItem?.id, currentPage, tableFilters, setRowSelection]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handleVinChangeForDropdown = useCallback((newValue) => {
    if (newValue) {
      handleFilterChange('mac_id', []);
    }
    handleFilterChange('vin', newValue ? [newValue] : []);
  }, [handleFilterChange]);

  const handleMacIdChangeForDropdown = useCallback((newValue) => {
    if (newValue) {
      handleFilterChange('vin', []);
    }
    handleFilterChange('mac_id', newValue ? [newValue] : []);
  }, [handleFilterChange]);

  const handleSavePreferences = useCallback(async () => {
    const isSaved = hasSavedPreferences();
    if (isSaved) {
      if (unsavePreferences()) {
        await savePreferencesToApi({ [PREF_STORAGE_KEY]: null });
        return { saved: false, message: 'Timeline filter unsaved.' };
      }
      return { saved: true, message: 'Could not unsave filter.' };
    }
    const prefsToSave = {
      vin: filters.vin || [],
      mac_id: filters.mac_id || [],
    };
    if (savePreferences(prefsToSave)) {
      await savePreferencesToApi({ [PREF_STORAGE_KEY]: prefsToSave });
      const label =
        filters.vin?.length > 0
          ? `VIN: ${filters.vin[0]}`
          : filters.mac_id?.length > 0
          ? `MAC: ${filters.mac_id[0]}`
          : 'current filter';
      return { saved: true, message: `Timeline filter saved (${label}).` };
    }
    return { saved: false, message: 'Could not save filter.' };
  }, [filters.vin, filters.mac_id, savePreferences, unsavePreferences, hasSavedPreferences, savePreferencesToApi]);

  const getOtherFilters = useCallback((excludeField) => {
    const other = {};
    if (excludeField !== 'vin' && filters.vin?.length > 0) {
      other.vin = filters.vin;
    }
    if (excludeField !== 'mac_id' && filters.mac_id?.length > 0) {
      other.mac_id = filters.mac_id;
    }
    return other;
  }, [filters]);

  const fieldMapping = useMemo(() => ({
    vin: 'vin',
    mac_id: 'mac_id',
  }), []);

  const fileDropdowns = useMemo(() => (
    <Stack spacing={0.5}>
      <FilterDropdown
        field="vin"
        label="VIN"
        value={filters.vin?.[0] || null}
        onChange={handleVinChangeForDropdown}
        customEndpoint="events/related-data?field=vin"
        multiple={false}
        disabled={filters.mac_id?.length > 0}
        icon={DirectionsCarIcon}
        dateFilters={{
          start_datetime: debouncedFilters.start_datetime,
          end_datetime: debouncedFilters.end_datetime,
          dateType,
          createIsoStringWithTimezone,
        }}
        otherFilters={getOtherFilters('vin')}
        fieldMapping={fieldMapping}
      />

      <FilterDropdown
        field="mac_id"
        label="MAC ID"
        value={filters.mac_id?.[0] || null}
        onChange={handleMacIdChangeForDropdown}
        customEndpoint="events/related-data?field=mac_id"
        multiple={false}
        disabled={filters.vin?.length > 0}
        icon={RouterIcon}
        dateFilters={{
          start_datetime: debouncedFilters.start_datetime,
          end_datetime: debouncedFilters.end_datetime,
          dateType,
          createIsoStringWithTimezone,
        }}
        otherFilters={getOtherFilters('mac_id')}
        fieldMapping={fieldMapping}
      />

      <FilterDropdown
        field="event_type"
        label="Event Type"
        value={filters.event_type || []}
        onChange={handleEventTypeChange}
        staticOptions={filterOptions.eventTypes}
        multiple={true}
        disabled={!hasVinOrMacId}
        isLoading={eventTypesQuery.isFetching}
        icon={EventIcon}
        getOptionLabel={(option) => option.displayText || `${option.value}`}
        isOptionEqualToValue={(option, value) => {
          const sameValueAndType = option.value === value.value && option.type === value.type;
          if (option.status != null && value.status != null) {
            return sameValueAndType && option.status === value.status;
          }
          return sameValueAndType;
        }}
        getOptionDisplayText={(option) => option.displayText || `${option.value}`}
      />

      <FilterDropdown
        field="category"
        label="Category"
        value={tableFilters.category || []}
        onChange={(newValue) => handleTableFilterChange('category', newValue || [])}
        staticOptions={filterOptions.categories}
        multiple={true}
        disabled={!selectedItem}
        isLoading={tableFilterOptionsQuery.isFetching}
        icon={CategoryIcon}
      />

      <FilterDropdown
        field="type"
        label="Type"
        value={tableFilters.type || []}
        onChange={(newValue) => handleTableFilterChange('type', newValue || [])}
        staticOptions={filterOptions.types}
        multiple={true}
        disabled={!selectedItem}
        isLoading={tableFilterOptionsQuery.isFetching}
        icon={LabelIcon}
      />
    </Stack>
  ), [
    filters, debouncedFilters, dateType, createIsoStringWithTimezone,
    handleVinChangeForDropdown, handleMacIdChangeForDropdown, handleEventTypeChange,
    getOtherFilters, fieldMapping, filterOptions, hasVinOrMacId,
    eventTypesQuery.isFetching, tableFilters, selectedItem,
    tableFilterOptionsQuery.isFetching, handleTableFilterChange,
  ]);

  const buslogFileOptions = useMemo(() => {
    const getCompactBuslogName = (fullPath) => {
      const fileName = String(fullPath || '').split(/[\\/]/).pop() || '';
      const match = fileName.match(/[+-]?(\d+_Cat-.*)$/);
      if (match?.[1]) {
        return match[1];
      }
      return fileName;
    };

    const results = timelineTableQuery.data?.data?.results || [];
    return results
      .filter(row => row.category === 'buslog' || row.file_type === 'buslog')
      .map((row) => {
        const fullPath = row.file_location || row.filename;
        const fileName = getCompactBuslogName(fullPath);
        const dateText = row.datetime ? formatDateTime(row.datetime) : '';
        if (!fullPath) return null;
        return {
          label: fileName,
          dateText,
          value: fullPath,
        };
      })
      .filter(Boolean);
  }, [timelineTableQuery.data, formatDateTime]);

  const filteredBuslogFileOptions = useMemo(() => {
    const search = buslogSearchTerm.trim().toLowerCase();
    if (!search) return buslogFileOptions;

    return buslogFileOptions.filter((option) => {
      const name = option.label?.toLowerCase() || '';
      const date = option.dateText?.toLowerCase() || '';
      return name.includes(search) || date.includes(search);
    });
  }, [buslogFileOptions, buslogSearchTerm]);

  const selectedBuslogLabel = useMemo(() => {
    if (!signalFilePath) return '';
    const selected = buslogFileOptions.find((option) => option.value === signalFilePath);
    return selected?.label || '';
  }, [buslogFileOptions, signalFilePath]);

  const selectedVehicleContext = useMemo(() => {
    if (filters?.vin?.[0]) {
      return `Selected VIN: ${filters.vin[0]}`;
    }
    if (filters?.mac_id?.[0]) {
      return `Selected MAC: ${filters.mac_id[0]}`;
    }
    return '';
  }, [filters?.vin, filters?.mac_id]);

  const signalDropdowns = useMemo(() => (
    <Stack spacing={1}>
      <Box className="buslog-picker">
        <Box className="buslog-picker-header">
          <GraphicEqIcon className="buslog-picker-title-icon" />
          <Typography variant="subtitle2" className="buslog-picker-title">Bus Log File</Typography>
        </Box>
        {!!selectedVehicleContext && (
          <Typography variant="caption" className="buslog-picker-context">
            {selectedVehicleContext}
          </Typography>
        )}
        <TextField
          size="small"
          fullWidth
          placeholder="Search bus log file..."
          value={buslogSearchTerm}
          onChange={(event) => setBuslogSearchTerm(event.target.value)}
          disabled={!selectedItem}
        />
        {!!selectedBuslogLabel && (
          <Typography variant="caption" className="buslog-picker-selected">
            Selected: {selectedBuslogLabel}
          </Typography>
        )}
        <Box className="buslog-picker-list">
          {!selectedItem ? (
            <Typography variant="caption" color="text.secondary" className="buslog-picker-empty">
              Select a timeline event first.
            </Typography>
          ) : timelineTableQuery.isFetching ? (
            <Box className="buslog-picker-loading">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Loading bus logs...</Typography>
            </Box>
          ) : filteredBuslogFileOptions.length === 0 ? (
            <Typography variant="caption" color="text.secondary" className="buslog-picker-empty">
              No matching bus log files.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {filteredBuslogFileOptions.map((option) => {
                const isSelected = option.value === signalFilePath;
                return (
                  <Box
                    key={option.value}
                    className={`buslog-picker-item${isSelected ? ' selected' : ''}`}
                    onClick={() => handleSignalFilePathChange(option.value)}
                  >
                    <Box className="buslog-picker-name-row">
                      <GraphicEqIcon className="buslog-picker-file-icon" />
                      <Typography variant="body2" className="buslog-picker-name">{option.label}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" className="buslog-picker-date">
                      {option.dateText || 'No date available'}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </Stack>
  ), [
    selectedVehicleContext, buslogSearchTerm, selectedItem, selectedBuslogLabel,
    timelineTableQuery.isFetching, filteredBuslogFileOptions, signalFilePath,
    handleSignalFilePathChange,
  ]);

  const dropdownContent = viewMode === 'signals' ? signalDropdowns : fileDropdowns;

  const getTimeDelta = useCallback((index, data) => {
    if (index >= data.length - 1) return null;

    const currentDate = new Date(data[index].datetime);
    const nextDate = new Date(data[index + 1].datetime);
    const diffMs = Math.abs(currentDate - nextDate);
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }
    if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    }

    return 'simultaneous';
  }, []);

  const sortBar = useMemo(() => (
    <Box className="sort-bar">
      <Chip
        label={sortDirection === 'desc' ? 'Newest first' : 'Oldest first'}
        size="small"
        variant="filled"
        color="primary"
        onClick={toggleSortDirection}
        icon={sortDirection === 'desc'
          ? <ArrowDownwardIcon className="sort-bar-icon" />
          : <ArrowUpwardIcon className="sort-bar-icon" />
        }
        className="sort-bar-chip"
      />
      {totalCount > 0 && (
        <Typography variant="caption" color="text.secondary" className="sort-bar-count">
          {totalCount.toLocaleString()} events
        </Typography>
      )}
    </Box>
  ), [sortDirection, toggleSortDirection, totalCount]);

  const renderMainContent = useCallback(() => {
    if (isLoading) return <LoadingState message="Loading timeline events..." />;
    if (isError) return (
      <ErrorState
        title="Error loading timeline events"
        message={`Something went wrong: ${error?.message || 'Unknown error'}`}
        onRetry={handleRetry}
      />
    );
    if (isEmpty) return (
      <EmptyState
        title="No timeline events found"
        subtitle="Try adjusting your filters to find events."
      />
    );

    return (
      <>
        {sortBar}
        <Box className="timeline-scroll-area">
          <Stack spacing={1.5} className="timeline-stack">
            {timelineData.map((item, index) => {
              const timeDelta = getTimeDelta(index, timelineData);

              return (
                <React.Fragment key={item.id}>
                  <TimelineCard
                    item={item}
                    onItemClick={handleTimelineItemClick}
                    isSelected={selectedItem?.id === item.id}
                    formatDateTime={formatDateTime}
                  />
                  {timeDelta && (
                    <Typography
                      variant="caption"
                      className="time-delta"
                    >
                      {timeDelta}
                    </Typography>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          {hasInfiniteNextPage && (
            <Box ref={loadMoreRef} className="load-more">
              {isFetchingNextPage ? (
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.primary">Loading more...</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Scroll to load more events...
                </Typography>
              )}
            </Box>
          )}

          {!hasInfiniteNextPage && hasData && (
            <Box className="timeline-end">
              <Divider className="timeline-divider" />
              <Typography variant="body2" color="text.disabled">
                You've reached the end of the timeline
              </Typography>
              <Typography variant="caption" color="text.disabled" className="total-count">
                Total: {totalCount} events loaded
              </Typography>
            </Box>
          )}
        </Box>
      </>
    );
  }, [
    isLoading,
    isError,
    error,
    handleRetry,
    isEmpty,
    sortBar,
    timelineData,
    getTimeDelta,
    handleTimelineItemClick,
    selectedItem,
    formatDateTime,
    hasInfiniteNextPage,
    loadMoreRef,
    isFetchingNextPage,
    hasData,
    totalCount,
  ]);

  const renderRightPanel = useCallback(() => {
    if (viewMode === 'signals') {
      if (!signalFilePath) {
        return (
          <Box className="center-content">
            <AutoGraphIcon className="center-state-icon" />
            <Typography variant="h6" color="text.primary">No file selected</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Select a bus log file from the filter panel or click the chart icon on a buslog file to visualize signals.
            </Typography>
          </Box>
        );
      }

      // BuslogPanels handles isEmpty, stepper, and empty-signals states internally
      return (
        <Box className="timeline-signal-view">
          <BuslogPanels
            prep={prep}
            signals={signals}
            playback={playback}
            playbackTimestamp={playbackTimestamp}
            filePath={signalFilePath}
          />
        </Box>
      );
    }

    if (!selectedItem) return (
      <Box className="center-content">
        <Typography variant="h6" color="text.primary">Select a timeline event</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Click on any timeline event on the left to view detailed information here.
        </Typography>
      </Box>
    );

    const isSelectionPending = selectedItem?.id !== deferredSelectedItem?.id;
    if (isSelectionPending || timelineTableQuery.isLoading) return <LoadingState message="Loading timeline details..." />;

    return (
      <Box className="table-container" ref={tableContainerRef}>
        <MaterialReactTable table={table} />
      </Box>
    );
  }, [
    viewMode,
    signalFilePath,
    prep,
    signals,
    playback,
    playbackTimestamp,
    selectedItem,
    deferredSelectedItem,
    timelineTableQuery.isLoading,
    table,
  ]);

  return (
    <ThemeProvider theme={props.activeTheme}>
      <Box className="main-container">
        <Box className="side-panel">
          <FiltersComponent
            activeTheme={props.activeTheme}
            dateRangePicker={{ filters: filters || {}, handleDateRangeChange }}
            timezoneSelector={{ timezone: userTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            viewModeSelector={{
              viewMode,
              handleViewModeChange,
              options: [
                { value: 'files', label: 'Files' },
                { value: 'signals', label: 'Signals' },
              ],
            }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdownContent}
            className="filters-component"
          />
        </Box>

        <Box className="main-panel">
          <Box className="main-panel-content">
            {renderMainContent()}
          </Box>
        </Box>

        <Box className="right-panel">
          {renderRightPanel()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TimelinePage;
